import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { 
  ArrowLeft, ArrowRight, Loader2, Clock, Target, Users, 
  Lightbulb, TrendingUp, PoundSterling, Megaphone, Wallet, 
  User, CheckCircle2, AlertCircle
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  "Target": Target,
  "Users": Users,
  "Lightbulb": Lightbulb,
  "TrendingUp": TrendingUp,
  "PoundSterling": PoundSterling,
  "Megaphone": Megaphone,
  "Wallet": Wallet,
  "User": User
};

export default function FullAssessmentPage() {
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const [sections, setSections] = useState([]);
  const [currentSection, setCurrentSection] = useState(0);
  const [answers, setAnswers] = useState({});
  const [businessName, setBusinessName] = useState("");
  const [oneLinePitch, setOneLinePitch] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showIntro, setShowIntro] = useState(true);

  useEffect(() => {
    fetchSections();
  }, []);

  const fetchSections = async () => {
    try {
      const response = await axios.get(`${API}/assessment/sections`);
      setSections(response.data.sections);
    } catch (error) {
      toast.error("Failed to load assessment");
    } finally {
      setLoading(false);
    }
  };

  const handleScoredAnswer = (sectionId, questionId, option) => {
    const key = `${sectionId}_${questionId}`;
    setAnswers(prev => ({
      ...prev,
      [key]: option.label
    }));
  };

  const handleTextAnswer = (fieldId, value) => {
    setAnswers(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };

  const handleNext = () => {
    if (currentSection < sections.length - 1) {
      setCurrentSection(prev => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  const handlePrevious = () => {
    if (currentSection > 0) {
      setCurrentSection(prev => prev - 1);
      window.scrollTo(0, 0);
    }
  };

  const isSectionComplete = (section) => {
    // Check all scored questions are answered
    for (const q of section.scored_questions) {
      const key = `${section.id}_${q.id}`;
      if (!answers[key]) return false;
    }
    return true;
  };

  const getCompletedSections = () => {
    return sections.filter(s => isSectionComplete(s)).length;
  };

  const handleSubmit = async () => {
    if (!user) {
      toast.error("Please sign in to submit your assessment");
      navigate("/auth?redirect=/assessment");
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/assessment/submit`,
        { 
          answers,
          business_name: businessName,
          one_line_pitch: oneLinePitch
        },
        { headers: getAuthHeaders() }
      );
      
      navigate(`/assessment/results/${response.data.assessment_id}`);
    } catch (error) {
      toast.error("Failed to submit assessment");
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Intro screen
  if (showIntro) {
    return (
      <div className="py-8 md:py-12">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8">
            <h1 className="text-2xl md:text-3xl font-bold mb-3">Full Assessment</h1>
            <p className="text-muted-foreground">
              Get your complete FoundrScore (0-100) with section-by-section breakdown
            </p>
          </div>

          <Card className="mb-6">
            <CardContent className="p-6">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">15 minutes</h3>
                  <p className="text-sm text-muted-foreground">8 sections, 24 scored questions</p>
                </div>
              </div>

              <div className="space-y-4 mb-6">
                <h4 className="font-medium">What you'll get:</h4>
                <ul className="space-y-2">
                  {[
                    "FoundrScore (0-100) with readiness band",
                    "Section-by-section breakdown with weights",
                    "2 strongest and 2 weakest areas highlighted",
                    "One-page business plan (rule-based)",
                    "Personalized next steps"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-amber-800">
                    <strong>Requires sign-in</strong> to save your results and generate your business plan.
                    {!user && (
                      <Button 
                        variant="link" 
                        className="p-0 h-auto text-amber-800 underline ml-1"
                        onClick={() => navigate("/auth?redirect=/assessment")}
                      >
                        Sign in now
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Business name and pitch inputs */}
              <div className="space-y-4 mb-6">
                <div>
                  <Label htmlFor="businessName">Business Name (optional)</Label>
                  <Input
                    id="businessName"
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                    placeholder="What's your business called?"
                    data-testid="business-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="oneLinePitch">One-Line Pitch (optional)</Label>
                  <Input
                    id="oneLinePitch"
                    value={oneLinePitch}
                    onChange={(e) => setOneLinePitch(e.target.value)}
                    placeholder="We help [customer] achieve [outcome] by [how]"
                    data-testid="one-line-pitch-input"
                  />
                </div>
              </div>

              <Button 
                className="w-full rounded-full"
                onClick={() => setShowIntro(false)}
                data-testid="start-assessment-btn"
              >
                Start Assessment
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const section = sections[currentSection];
  const SectionIcon = ICON_MAP[section?.icon] || Target;
  const progress = ((currentSection + 1) / sections.length) * 100;
  const completedSections = getCompletedSections();
  const allComplete = completedSections === sections.length;

  return (
    <div className="py-8 md:py-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">
              Section {currentSection + 1} of {sections.length}
            </span>
            <span className="text-sm text-muted-foreground">
              {completedSections}/{sections.length} complete
            </span>
          </div>
          <Progress value={progress} className="h-2" data-testid="assessment-progress" />
        </div>

        {/* Section indicator tabs */}
        <div className="flex gap-1 mb-6 overflow-x-auto pb-2">
          {sections.map((s, index) => {
            const Icon = ICON_MAP[s.icon] || Target;
            const isComplete = isSectionComplete(s);
            return (
              <button
                key={s.id}
                onClick={() => setCurrentSection(index)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                  index === currentSection
                    ? "bg-primary text-primary-foreground"
                    : isComplete
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                }`}
                data-testid={`section-tab-${index}`}
              >
                <Icon className="w-3 h-3" />
                <span className="hidden sm:inline">{s.title}</span>
                {isComplete && <CheckCircle2 className="w-3 h-3" />}
              </button>
            );
          })}
        </div>

        {/* Section Card */}
        <Card className="mb-6" data-testid="assessment-section-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <SectionIcon className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-lg">{section?.title}</CardTitle>
                <CardDescription>
                  {section?.description} • Weight: {section?.weight}/100
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-8">
            {/* Scored Questions */}
            {section?.scored_questions?.map((question, qIndex) => (
              <div key={question.id} className="space-y-4" data-testid={`question-${question.id}`}>
                <div className="flex items-start gap-2">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs font-medium">
                    {qIndex + 1}
                  </span>
                  <div>
                    <p className="font-medium">{question.question}</p>
                    {question.is_evidence && (
                      <span className="text-xs text-amber-600 font-medium">
                        Evidence question (counts 2×)
                      </span>
                    )}
                  </div>
                </div>
                
                <RadioGroup
                  value={answers[`${section.id}_${question.id}`] || ""}
                  onValueChange={(value) => {
                    const option = question.options.find(o => o.label === value);
                    if (option) handleScoredAnswer(section.id, question.id, option);
                  }}
                  className="space-y-2 ml-8"
                >
                  {question.options.map((option) => (
                    <div key={option.label} className="relative">
                      <RadioGroupItem
                        value={option.label}
                        id={`${question.id}-${option.label}`}
                        className="peer sr-only"
                      />
                      <Label
                        htmlFor={`${question.id}-${option.label}`}
                        className="quiz-option peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5"
                        data-testid={`option-${question.id}-${option.label}`}
                      >
                        <div className="flex items-start gap-3">
                          <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
                            answers[`${section.id}_${question.id}`] === option.label
                              ? 'bg-primary text-white'
                              : 'bg-muted text-muted-foreground'
                          }`}>
                            {option.label}
                          </span>
                          <span className="text-sm leading-relaxed">{option.text}</span>
                        </div>
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            ))}

            {/* Text Fields */}
            {section?.text_fields?.map((field) => (
              <div key={field.id} className="space-y-2" data-testid={`text-field-${field.id}`}>
                <Label htmlFor={field.id}>{field.label}</Label>
                <Textarea
                  id={field.id}
                  value={answers[field.id] || ""}
                  onChange={(e) => handleTextAnswer(field.id, e.target.value)}
                  placeholder={field.placeholder}
                  rows={3}
                  className="resize-none"
                />
                <p className="text-xs text-muted-foreground">
                  This feeds into your one-page business plan
                </p>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentSection === 0}
            className="rounded-full"
            data-testid="assessment-prev-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          {currentSection === sections.length - 1 ? (
            <Button
              onClick={handleSubmit}
              disabled={!allComplete || submitting}
              className="rounded-full px-6"
              data-testid="assessment-submit-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Calculating...
                </>
              ) : (
                <>
                  Get My FoundrScore
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              className="rounded-full"
              data-testid="assessment-next-btn"
            >
              Next Section
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>

        {!allComplete && currentSection === sections.length - 1 && (
          <p className="text-center text-sm text-muted-foreground mt-4">
            Complete all sections to submit your assessment
          </p>
        )}
      </div>
    </div>
  );
}
